import React, { useState } from "react";

import { createToast } from "Utils/toastFunc";
import { LoadingText } from "Components/LoadingText";
import { CartDetailValue } from "Pages/ServiceForm/components/Cart/CartDetailValue";
import { Button } from "Components/Button";
import { History } from "lucide-react";
import { useItemsContext } from "../../../hooks/useItemsContext";
import { CartListItems } from "./CartAttendmenList";
import { UnifiedProductModal } from "../../UnifiedProductModal/index";
import { NewTrackingDisplay } from "./NewTrackingDisplay";
import { ProductWithSource } from "../../../interface";

export const CartSummary = () => {
  const [loadingOperation, setLoadingOperation] = useState<null | number>(null);
  const [replacementModal, setReplacementModal] = useState<{
    isOpen: boolean;
    product: ProductWithSource | null;
  }>({
    isOpen: false,
    product: null,
  });
  const [showTracking, setShowTracking] = useState(false);

  const {
    items,
    addQuantityItem,
    deleteQuantityItem,
    replaceItem,
    getTrackHistory,
    getNewTrackHistory,
    notFoundProducts,
  } = useItemsContext();

  const addUnitItem = async (productId: number) => {
    setLoadingOperation(productId);
    try {
      addQuantityItem(productId);
      createToast("success", "Quantidade atualizada com sucesso!");
    } catch (error) {
      createToast("error", "Erro ao atualizar quantidade");
    } finally {
      setLoadingOperation(null);
    }
  };

  const removeUnitItem = async (productId: number) => {
    setLoadingOperation(productId);
    try {
      deleteQuantityItem(productId);
      createToast("success", "Quantidade atualizada com sucesso!");
    } catch (error) {
      createToast("error", "Erro ao atualizar quantidade");
    } finally {
      setLoadingOperation(null);
    }
  };

  const calculateTotal = () => {
    return items.reduce(
      (total, item) => total + item.product_price * item.amount,
      0
    );
  };

  // Função para lidar com substituição de produtos
  const handleReplaceProduct = (productId: number) => {
    const product = items.find((item) => item.product_id === productId);
    if (product) {
      setReplacementModal({
        isOpen: true,
        product,
      });
    }
  };

  // Função para confirmar substituição
  const handleConfirmReplacement = (newProducts: ProductWithSource[]) => {
    if (replacementModal.product) {
      replaceItem(replacementModal.product.product_id, newProducts);
      createToast(
        "success",
        `${newProducts.length} produto(s) substituído(s) com sucesso!`
      );
    }
  };

  // Função para fechar modal
  const handleCloseReplacementModal = () => {
    setReplacementModal({
      isOpen: false,
      product: null,
    });
  };

  // Função para visualizar tracking
  const handleViewTracking = () => {
    setShowTracking(!showTracking);
  };

  const trackHistory = getTrackHistory();
  const newTrackHistory = getNewTrackHistory();

  // Função para determinar se deve mostrar o botão de histórico
  const shouldShowHistoryButton = () => {
    if (newTrackHistory) {
      return (
        newTrackHistory.items.length > 0 ||
        newTrackHistory.added_products.length > 0 ||
        newTrackHistory.not_founded.length > 0
      );
    }
    return trackHistory.length > 0;
  };

  // Função para contar total de alterações
  const getTotalChanges = () => {
    if (newTrackHistory) {
      const changedItems = newTrackHistory.items.filter(
        (item) => item.final_status !== "kept"
      ).length;
      return (
        changedItems +
        newTrackHistory.added_products.length +
        newTrackHistory.not_founded.length
      );
    }
    return trackHistory.length;
  };

  return (
    <div className="tab-item">
      <div className="cart-header">
        <h5 className="text-center">
          Resumo:{" "}
          <span>
            {items.length || 0} {items.length === 1 ? "item" : "itens"}
          </span>{" "}
          no orçamento
        </h5>

        {shouldShowHistoryButton() && (
          <Button onClick={handleViewTracking} outlined>
            <History size={16} />
            Histórico de Alterações ({getTotalChanges()})
          </Button>
        )}
      </div>

      {/* Histórico de Tracking - Novo Modelo Ativo */}
      {showTracking && newTrackHistory && (
        <NewTrackingDisplay trackingData={newTrackHistory} />
      )}

      {/* Histórico de Tracking - Modelo Antigo (Fallback) */}
      {showTracking && !newTrackHistory && trackHistory.length > 0 && (
        <div className="tracking-history">
          <h6>Histórico de Alterações:</h6>
          {trackHistory.map((track, index) => (
            <div key={index} className="track-item">
              <div className="track-original">
                <strong>Original:</strong>{" "}
                {track.produto_original.produto_original_nome}
                {track.produto_original.produto_original_match_nome !==
                  track.produto_original.produto_original_nome && (
                  <span className="match-info">
                    (Encontrado:{" "}
                    {track.produto_original.produto_original_match_nome})
                  </span>
                )}
              </div>
              <div className="track-corrections">
                <strong>Substituído por:</strong>
                {track.items_corretos.length > 0 ? (
                  <ul>
                    {track.items_corretos.map((correction, corrIndex) => (
                      <li key={corrIndex}>
                        {correction.produto_corrigido_nome} (ID:{" "}
                        {correction.produto_corrigido_id})
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="removed">Produto removido</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {loadingOperation ? (
        <LoadingText text="Atualizando quantidade..." />
      ) : (
        <>
          <CartListItems
            items={items}
            addUnitItem={addUnitItem}
            removeUnitItem={removeUnitItem}
            loadingOperation={loadingOperation}
            onReplace={handleReplaceProduct}
            notFoundProducts={notFoundProducts}
          />

          {items.length > 0 && (
            <div
              className="total-cart-holder-new-layout"
              style={{ marginTop: "15px" }}
            >
              <CartDetailValue
                label="Total"
                value={`R$ ${calculateTotal().toFixed(2).replace(".", ",")}`}
              />
            </div>
          )}
        </>
      )}

      {/* Modal de Substituição */}
      {replacementModal.isOpen && replacementModal.product && (
        <UnifiedProductModal
          isOpen={replacementModal.isOpen}
          onClose={handleCloseReplacementModal}
          mode="replacement"
          originalProduct={replacementModal.product}
          onConfirmReplacement={handleConfirmReplacement}
        />
      )}

      <style>{`
        .tracking-history {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 16px;
          margin: 16px 0;
          max-height: 400px;
          overflow-y: auto;
        }

        .track-section {
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid #dee2e6;
        }

        .track-section:last-child {
          border-bottom: none;
          margin-bottom: 0;
        }

        .track-section h7 {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #495057;
          margin-bottom: 12px;
          background: #fff;
          padding: 8px 12px;
          border-radius: 6px;
          border-left: 4px solid #007bff;
        }

        .exams-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .exam-tag {
          background: #e3f2fd;
          color: #1565c0;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          border: 1px solid #bbdefb;
        }

        .track-item {
          background: #fff;
          border: 1px solid #e9ecef;
          border-radius: 6px;
          padding: 12px;
          margin-bottom: 12px;
        }

        .track-original {
          margin-bottom: 8px;
          font-size: 14px;
        }

        .track-original strong {
          color: #495057;
        }

        .match-info {
          color: #6c757d;
          font-size: 12px;
          margin-left: 8px;
          font-style: italic;
        }

        .confidence-info {
          color: #28a745;
          font-size: 11px;
          margin-left: 8px;
          font-weight: 500;
        }

        .track-status {
          margin-bottom: 8px;
        }

        .track-status strong {
          color: #495057;
        }

        .status-kept {
          color: #28a745;
          font-weight: 500;
          margin-left: 8px;
        }

        .status-replaced {
          color: #ffc107;
          font-weight: 500;
          margin-left: 8px;
        }

        .status-removed {
          color: #dc3545;
          font-weight: 500;
          margin-left: 8px;
        }

        .track-replacements {
          background: #f8f9fa;
          padding: 8px;
          border-radius: 4px;
          margin-top: 8px;
        }

        .track-replacements strong {
          color: #495057;
          font-size: 13px;
        }

        .track-replacements ul {
          margin: 8px 0 0 0;
          padding-left: 20px;
        }

        .track-replacements li {
          font-size: 13px;
          color: #6c757d;
        }

        .not-found-item {
          color: #dc3545;
          font-size: 13px;
          font-style: italic;
        }

        .track-corrections {
          margin-top: 8px;
        }

        .track-corrections strong {
          color: #495057;
        }

        .removed {
          color: #dc3545;
          font-style: italic;
        }

        .tracking-history ul {
          margin: 8px 0;
          padding-left: 20px;
        }

        .tracking-history li {
          font-size: 13px;
          color: #6c757d;
          margin-bottom: 4px;
        }
      `}</style>
    </div>
  );
};
